import { Model } from '@vuex-orm/core';
import { clientLocationEntity } from './client-location-entity';
import { clientTypeEntity } from './client-types-entity';
import { clientWebDomainEntity } from './client-web-domain-entity';
import { contactEntity } from './contact-entity';
import { clientStatusEntity } from './enum-entities';
import { minimizedItemEntity } from './minimized-item-entity';
import { ticketEntity } from './ticket-entity';

function clientAttrDateToJs(value: string | undefined | null): Date | null {
    if (value != null) {
        return new Date(value);
    }
    return null;
}

/**
 * Instance shape for attributes defined in `fields()`.
 * Merged with the class name so `this` in getters and query results stay typed
 * without emitting class fields (compatible with `useDefineForClassFields: true`).
 */
export interface clientEntity {
    clientId: number;
    loggedIn: boolean;
    clientName: string;
    billingName: string;
    clientStatusId: number;
    serviceProviderId: number;
    contactsCount: number;
    tick?: number;
    createdOn: string;
    updatedOn?: string;
    antivirus: number;
    antivirusConfig?: string | null;
    clientStatus: clientStatusEntity;
    clientType: clientTypeEntity;
    isFilling: boolean;
    xeroClientId: string;
    isActionButtonOpen: boolean;
    clientTypeId: number;
    hasCompanyBanner: boolean;
    companyBanner: string;
    alternateName?: string;
    isBlockHoursWarningThresholdReached: boolean;
    isAccessible: boolean;
    trackingCategoryJson?: string;
    xeroBrandingThemeId?: string;
    isBlockHoursExpired: boolean;
    isDefault: boolean;
    pax8Id: string | null;
    locations: any[] | null;
}

export class clientEntity extends Model {
    static entity = 'clientEntity';
    static primaryKey = 'clientId';
    static fields() {
        return {
            clientId: this.number(0),
            loggedIn: this.boolean(false),
            clientName: this.string(''),
            billingName: this.string(''),
            clientStatusId: this.number(0),
            serviceProviderId: this.number(0),
            contactsCount: this.number(0),
            tick: this.number(0),
            createdOn: this.attr(''),
            updatedOn: this.attr(''),
            antivirus: this.number(0),
            antivirusConfig: this.attr(null),
            clientStatus: this.hasOne(clientStatusEntity, 'id', 'clientStatusId'),
            clientType: this.hasOne(clientTypeEntity, 'clientTypeId', 'clientTypeId'),
            isFilling: this.boolean(true),
            xeroClientId: this.attr(''),
            isActionButtonOpen: this.boolean(false),
            clientTypeId: this.number(0),
            hasCompanyBanner: this.boolean(false),
            companyBanner: this.string(''),
            alternateName: this.string('').nullable(),
            isBlockHoursWarningThresholdReached: this.boolean(false),
            isAccessible: this.boolean(true),
            trackingCategoryJson: this.string(''),
            xeroBrandingThemeId: this.string(''),
            isBlockHoursExpired: this.boolean(false),
            isDefault: this.boolean(false),
            pax8Id: this.attr(null),
            locations: this.attr(null),
        };
    }

    static afterDelete(record: any) {
        minimizedItemEntity.delete((row: any) => {
            return row.itemId == record.clientId;
        });
    }

    static beforeSelect(records: any) {
        return records.filter((row: any) => row.clientId != 0);
    }

    get totalContactsCount() {
        return contactEntity.query().where('clientId', this.clientId).where('status', 'Active').count();
    }

    get totalActiveAndOpenTickets() {
        return ticketEntity
            .query()
            .where('clientId', this.clientId)
            .where('ticketStatusId', [1, 2, 3, 6])
            .count();
    }

    get clientLocations() {
        return clientLocationEntity.query<any>().where('clientId', this.clientId).orderBy('nameForSort').all();
    }

    get firstLocation() {
        return this.clientLocations[0];
    }

    get webDomains() {
        return clientWebDomainEntity.query<any>().where('clientId', this.clientId);
    }

    get clientNameForSort() {
        return this.clientName.toLowerCase();
    }

    get isClientMappedWithSite() {
        if (this.antivirusConfig && this.antivirusConfig != '' && this.antivirus != 0) {
            return true;
        }
        return false;
    }

    get jsCreatedOn() {
        return clientAttrDateToJs(this.createdOn);
    }

    get jsUpdatedOn() {
        return clientAttrDateToJs(this.updatedOn);
    }

    get getAlternateNames() {
        if (this.alternateName && this.alternateName != 'null') {
            return this.alternateName.split(',');
        }
        return [];
    }

    get clientNameWithAlternateNames() {
        if (this.getAlternateNames.length > 0) {
            return `${this.clientName} (${this.getAlternateNames.join(', ')})`;
        }
        return this.clientName;
    }

    get clientNameWithAlternateNamesForSort() {
        return this.clientNameWithAlternateNames.toLowerCase();
    }

    get clientTypeName() {
        const clientTname: any = clientTypeEntity.find(this.clientTypeId);
        return clientTname.clientTypeName;
    }

    get clientStatusName() {
        const clientSname: any = clientStatusEntity.query<any>().where('id', this.clientStatusId).first();
        return clientSname.name;
    }
}
